import React, { useRef, useState, FC } from 'react';
import Slider from 'react-slick';
import LazySection from '../@shared/lazySection';
import { windowService } from '../../utils/window.utils';
import { getCustomPaging } from '../@shared/carouselDots';
import { TCardGridProps } from './types/cardGrid.types';
import { CardGridModel } from './models/cardGrid.model';
import Heading from '../@shared/heading';
import CardGridCard from './components/cardGridCard';
import CarouselArrow from '../@shared/carouselArrow';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import appendDots from '../@shared/customCarouselDots/appendDots';

// use only in useEffect / functions that runs only client-side
const getSlidesToShow = (): number => {
  const breakpointMd = windowService().matchMedia('(min-width: 450px)').matches;
  if (breakpointMd) {
    const breakpointLg = windowService().matchMedia('(min-width: 1000px)').matches;
    if (breakpointLg) {
      return 4;
    }
    return 2;
  }
  return 1.2;
};

const CardGrid: FC<TCardGridProps> = (props) => {
  const model = new CardGridModel(props);
  const cardsCount = model?.cards?.length;
  const generateCards = model?.cards?.map((item) => <CardGridCard {...item} key={item.id} cardHeadingTag={model.cardHeadingTitleTag} />);
  const [activeCardIndex, updateActiveCardIndex] = useState(0);

  const cardGridWrapperRef = useRef<HTMLDivElement>(null);

  const settings = {
    arrows: true,
    dots: true,
    slidesToScroll: 1,
    slidesToShow: 4,
    nextArrow: <CarouselArrow direction="next" />,
    prevArrow: <CarouselArrow direction="previous" />,
    customPaging: getCustomPaging,
    infinite: false,
    responsive: [
      { breakpoint: 1000, settings: { slidesToShow: 2 } },
      { breakpoint: 450, settings: { slidesToShow: 1.2 } },
    ],
    appendDots: (dots) => appendDots({ dots, cardsCount, activeCardIndex, showCustomDots: cardsCount > 5 }),
  };

  const activeClass = 'slick-active';

  const isLastDecimalSlide = (currentSlide): boolean => {
    const slidesToShow = getSlidesToShow();
    const slideTotal = model.cards.length;
    return +currentSlide.toFixed(2) === +(slideTotal - slidesToShow).toFixed(2);
  };

  const beforeChange = function (_, nextSlide): void {
    updateActiveCardIndex(nextSlide);
    if (cardGridWrapperRef.current) {
      const lastPaginationEl = (cardGridWrapperRef.current as HTMLElement).querySelector('.slick-dots li:last-child');
      if (lastPaginationEl && isLastDecimalSlide(nextSlide)) {
        lastPaginationEl?.classList.add(activeClass);
      } else {
        lastPaginationEl?.classList.remove(activeClass);
      }
    }
  };

  const afterChange = function (currentSlide: number): void {
    if (cardGridWrapperRef.current) {
      const lastPaginationEl = (cardGridWrapperRef.current as HTMLElement).querySelector('.slick-dots li:last-child');
      if (lastPaginationEl && isLastDecimalSlide(currentSlide)) {
        lastPaginationEl?.classList.add(activeClass);
      } else {
        lastPaginationEl?.classList.remove(activeClass);
      }
    }
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <LazySection>
      <div className="card-grid-wrapper" ref={cardGridWrapperRef}>
        <div className="card-grid">
          <div className="card-grid__text">
            <Heading title={model.title} subtitle={model.subtitle} editable={true} hideSecondaryLink={true} tag={model.headingTag} />
          </div>
          <Slider {...settings} beforeChange={beforeChange} afterChange={afterChange}>
            {generateCards}
          </Slider>
        </div>
      </div>
    </LazySection>
  );
};

export default CardGrid;
